import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { Box, Chip, Typography } from "@mui/material";
import { JobDetailDeliveryVerification, JobDetailViewModel } from "./jobDetailTypes";
import { DetailCard } from "./DetailCard";

type Props = {
  job: JobDetailViewModel;
};

function DeltaChip({ delta }: { delta: number }) {
  if (delta === 0) {
    return (
      <Chip
        label="Matches estimate"
        size="small"
        sx={{
          height: 22,
          fontSize: 11,
          fontWeight: 600,
          backgroundColor: "#ECEFF1",
          color: "#546E7A",
        }}
      />
    );
  }

  const isUp = delta > 0;
  return (
    <Chip
      label={`${isUp ? "+" : ""}${delta} vs. client estimate`}
      size="small"
      sx={{
        height: 22,
        fontSize: 11,
        fontWeight: 600,
        backgroundColor: isUp ? "#E8F5E9" : "#FFEBEE",
        color: isUp ? "#2E7D32" : "#C62828",
      }}
    />
  );
}

export default function DeliveryVerifiedCard({ job }: Props) {
  const verification = job.deliveryVerification as JobDetailDeliveryVerification;

  return (
    <DetailCard title="Delivery Verified">
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 1.25,
          backgroundColor: "#E3F2FD",
          borderRadius: "8px",
          px: 2,
          py: 1.5,
          mb: 2.5,
        }}
      >
        <CheckCircleIcon sx={{ color: "#1565C0", fontSize: 22, mt: 0.1 }} />
        <Box>
          <Typography fontWeight={700} sx={{ fontSize: 13, letterSpacing: 0.4, color: "#0D47A1" }}>
            {verification.verifiedOnLabel}
          </Typography>
          <Typography sx={{ fontSize: 12, color: "#1565C0", mt: 0.25 }}>{verification.inboundNote}</Typography>
        </Box>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 1.5, fontWeight: 700, letterSpacing: 0.8 }}
      >
        FINAL COUNT — VERIFIED
      </Typography>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
          gap: 1.5,
          mb: 2.5,
        }}
      >
        {verification.countSheetItems.map((item) => (
          <Box
            key={item.label}
            sx={{
              border: "1px solid #ECECEC",
              borderRadius: "10px",
              p: 2,
              backgroundColor: "background.paper",
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700, letterSpacing: 0.8, display: "block", mb: 0.5 }}
            >
              {item.label}
            </Typography>
            <Typography fontWeight={700} sx={{ fontSize: 32, lineHeight: 1.1, mb: 1 }}>
              {item.verifiedCount}
            </Typography>
            <DeltaChip delta={item.delta} />
          </Box>
        ))}
      </Box>

      <Box
        sx={{
          borderTop: "1px solid #ECECEC",
          pt: 2,
          display: "grid",
          gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5, fontWeight: 700, letterSpacing: 0.8 }}
          >
            PICKED UP ON
          </Typography>
          <Typography fontWeight={600} sx={{ fontSize: 14 }}>
            {verification.pickedUpOnLabel}
          </Typography>
        </Box>
        <Box>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block", mb: 0.5, fontWeight: 700, letterSpacing: 0.8 }}
          >
            VENDOR
          </Typography>
          <Typography fontWeight={600} sx={{ fontSize: 14 }}>
            {verification.vendorSummaryLabel}
          </Typography>
        </Box>
      </Box>
    </DetailCard>
  );
}
